import { goto } from '$app/navigation'
import type { ToastStore } from '@skeletonlabs/skeleton'
import camelcaseKeys from 'camelcase-keys'
import { getApps } from 'firebase/app'
import type { User as FirebaseUser } from 'firebase/auth'
import {
  getAuth,
  getIdToken,
  indexedDBLocalPersistence,
  onAuthStateChanged,
  setPersistence,
  signOut,
} from 'firebase/auth'

import { loggedInStore, type LoginResult, User } from '@stores/logged-in-store'
import { getConfig } from '@utils/config.svelte'
import type { Config } from '@utils/config.svelte'
import { handleFetch, setCachedAirGappedUserPassword } from '@utils/fetch'
import { resetLabelStudioSessionState } from '@utils/label-studio'

type AuthProvider = 'airGapped' | 'fakeLogin' | 'firebase' | 'workos'

const HIRUNDO_EMAIL_ENDING = '@hirundo.io'
const WORKOS_AUTH_BASE_PATH = '/user/workos'

const isHirundoEmail = (email: string | undefined): boolean =>
  email?.toLowerCase()?.endsWith(HIRUNDO_EMAIL_ENDING) ?? false

const airGappedUserPasswordLocalStorage = 'airGappedUserPassword'
const setAirGappedUserPassword = (username: string, password: string) => {
  const encodedPassword = btoa(`${username}:${password}`)
  localStorage.setItem(airGappedUserPasswordLocalStorage, encodedPassword)
  setCachedAirGappedUserPassword(encodedPassword)
}

const PRE_AUTH_ROUTES = ['/login', '/signup']

const sanitizeReturnTo = (returnTo: string | null): string | null => {
  if (!returnTo) {
    return null
  }
  try {
    // NOTE: This is a sentinel origin used only for parsing and validation.
    // `new URL(absoluteUrl, sentinelOrigin)` ignores the base and keeps the absolute origin.
    // That allows us to safely reject open redirects (e.g. https://evil.com/...).
    const sentinelOrigin = 'http://hirundo.local'
    const parsed = new URL(returnTo, sentinelOrigin)
    if (parsed.origin !== sentinelOrigin) {
      return null
    }
    const sanitized = `${parsed.pathname}${parsed.search}${parsed.hash}`
    if (!sanitized.startsWith('/') || sanitized.startsWith('//')) {
      return null
    }
    if (PRE_AUTH_ROUTES.includes(parsed.pathname)) {
      return null
    }
    return sanitized
  } catch {
    return null
  }
}

let addedTokenChangeListener = false
let activeAuthProvider: AuthProvider = 'firebase'

const setActiveAuthProvider = (authProvider: AuthProvider) => {
  activeAuthProvider = authProvider
}

const buildWorkOSAuthPath = (path: 'login' | 'logout' | 'session') =>
  `${WORKOS_AUTH_BASE_PATH}/${path}`

const buildWorkOSAuthUrl = (
  apiBaseUrl: string,
  path: 'login' | 'logout' | 'session',
  searchParams?: URLSearchParams,
) => {
  const normalizedApiBaseUrl = apiBaseUrl.replace(/\/$/, '')
  const queryString = searchParams ? `?${searchParams}` : ''
  return `${normalizedApiBaseUrl}${buildWorkOSAuthPath(path)}${queryString}`
}

const getAuthProvider = (config: Config): AuthProvider => {
  if (config.fakeLogin) {
    return 'fakeLogin'
  }
  if (config.airGapped) {
    return 'airGapped'
  }
  return config.workosEnabled ? 'workos' : 'firebase'
}

const clearLocalAuthState = () => {
  localStorage.setItem('authUser', '')
  loggedInStore.update(User.logout())
  resetLabelStudioSessionState()
}

const persistLoginResult = (email: string, loginResult: LoginResult) => {
  localStorage.setItem('authUser', JSON.stringify({ email, loginResult }))
  loggedInStore.update(User.login(email, loginResult))
}

const getCurrentLoginUrl = () => {
  const currentPath =
    typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}${window.location.hash}`
      : null
  const returnTo = sanitizeReturnTo(currentPath)
  return returnTo ? `/login?returnTo=${encodeURIComponent(returnTo)}` : '/login'
}

const forceWorkOSLogout = async (goToLogin = true) => {
  try {
    const res = await handleFetch(buildWorkOSAuthPath('logout'), { method: 'POST' })
    const data = await res.json().catch(() => ({}))
    clearLocalAuthState()
    if (goToLogin) {
      const logoutUrl = data?.logoutUrl && data.logoutUrl !== '/login' ? data.logoutUrl : '/login'
      window.location.href = logoutUrl
      return
    }
  } catch (error) {
    console.error('Failed to sign out with WorkOS', error)
    clearLocalAuthState()
    if (goToLogin) {
      window.location.href = '/login'
    }
  }
}

const forceFirebaseLogout = async (goToLogin = true) => {
  try {
    await handleFetch('/user/logout')
    if (getApps().length > 0) {
      const auth = getAuth()
      await signOut(auth)
    }
    if (goToLogin) {
      await goto(getCurrentLoginUrl(), { invalidateAll: true })
    }
  } catch (error) {
    console.error('Failed to sign out with Firebase', error)
  }
  clearLocalAuthState()
}

const forceLogout = async (goToLogin = true, authProvider: AuthProvider = activeAuthProvider) => {
  if (authProvider === 'workos') {
    await forceWorkOSLogout(goToLogin)
    return
  }
  await forceFirebaseLogout(goToLogin)
}

const restoreWorkOSSession = async (toastStore?: ToastStore) => {
  try {
    const config = await getConfig(toastStore)
    const res = await fetch(buildWorkOSAuthUrl(config.apiBaseUrl, 'session'), {
      credentials: 'include',
    })
    if (res.status === 401) {
      return false
    }
    if (!res.ok) {
      console.error('Unexpected WorkOS session response', res.status, res.statusText)
      return false
    }
    const loginResult = await res.json()
    const email = loginResult.username as string
    persistLoginResult(email, camelcaseKeys(loginResult))
    return true
  } catch (error) {
    console.error('Failed to restore WorkOS session', error)
    return false
  }
}

const checkWorkOSAuth = async (toastStore?: ToastStore) => {
  const restored = await restoreWorkOSSession(toastStore)
  if (!restored) {
    await forceWorkOSLogout()
  }
  return restored
}

const refreshIdToken = async (user: FirebaseUser, toastStore: ToastStore, forceRefresh = false) => {
  const idToken = (await getIdToken(user, forceRefresh)) ?? ''
  if (!idToken) {
    return await forceFirebaseLogout()
  }
  try {
    const loginResult = await (
      await handleFetch('/user/', {
        method: 'POST',
        body: { idToken },
      })
    ).json()
    if (!user.email) {
      console.error('Failed to get email from user info', user)
      toastStore?.trigger({
        message: 'Failed to user info. Please contact Hirundo support.',
        background: 'variant-filled-error',
      })
      return
    }
    persistLoginResult(user.email, camelcaseKeys(loginResult))
  } catch (error) {
    console.error('Failed to get user info', error)
    return await forceFirebaseLogout()
  }
}

const onIdTokenRevocation = (toastStore: ToastStore) => async (user: FirebaseUser | null) => {
  if (user) {
    await refreshIdToken(user, toastStore)
    return
  }
  // For an email/password user. Prompt the user for the password again.
  toastStore?.trigger({
    message: 'Please re-authenticate',
    background: 'variant-filled-warning',
  })
  return await forceFirebaseLogout()
}

const checkAuth = async (toastStore: ToastStore) => {
  const auth = getAuth()
  setPersistence(auth, indexedDBLocalPersistence)
  // ⬆️ Changed from browserLocalPersistence to indexedDBLocalPersistence per this issue: https://github.com/firebase/firebase-js-sdk/issues/8282
  await auth.authStateReady()
  if (!auth.currentUser) {
    await forceFirebaseLogout()
    return
  }

  if (!addedTokenChangeListener) {
    onAuthStateChanged(auth, onIdTokenRevocation(toastStore))
    addedTokenChangeListener = true
  }
  await refreshIdToken(auth.currentUser, toastStore, true)
  setInterval(
    () => {
      if (auth.currentUser) {
        refreshIdToken(auth.currentUser, toastStore)
      }
    },
    59 * 60 * 1_000,
  )
  // ⬆️ Refresh the token automatically every hour (at 59 mins) to avoid the user being logged out
  //   when making a request after the token has expired
}

const fakeLogin = async (toastStore?: ToastStore) => {
  const loginResult = await (
    await handleFetch(
      '/user/fake-login',
      {
        method: 'POST',
      },
      '',
      toastStore,
    )
  ).json()
  const email = loginResult.username as string
  persistLoginResult(email, camelcaseKeys(loginResult))
  return loginResult
}

export {
  airGappedUserPasswordLocalStorage,
  type AuthProvider,
  buildWorkOSAuthUrl,
  checkAuth,
  checkWorkOSAuth,
  fakeLogin,
  forceFirebaseLogout,
  forceLogout,
  forceWorkOSLogout,
  getAuthProvider,
  HIRUNDO_EMAIL_ENDING,
  isHirundoEmail,
  PRE_AUTH_ROUTES,
  restoreWorkOSSession,
  sanitizeReturnTo,
  setActiveAuthProvider,
  setAirGappedUserPassword,
}
